import React from 'react';
import { Stack, Text, Button, Divider, useBreakpoint, ControlLink } from '@moda/om';
import { formatPrice } from '@moda/portal-stanchions';
import { hrefFor } from '../../routers';
import { useCart, useCartItems } from '../../hooks/useCart';
import {
  CheckoutAmazonPayButton,
  CheckoutApplePayButton,
  CheckoutPayPalButton
} from '../CheckoutPaymentSources';
import { AffirmPromoMessage } from '../AffirmPromoMessage';
import { RewardSummary } from '../Rewards';
import { GiftCode } from '../GiftCode';
import { useInternationalDomesticShippingFeeOptimization } from '../../hooks/useInternationalDomesticShippingFeeOptimization';
import { useUsDomesticShippingFeeOptimization } from '../../hooks/useUsDomesticShippingFeeOptimization';
import { PaymentSummaryRow } from './PaymentSummaryRow';
import { PaymentSummaryTaxRow } from './PaymentSummaryTaxRow';

import './ShoppingBagPaymentSummary.scss';

export const ShoppingBagPaymentSummary: React.FC = () => {
  const {
    cart: {
      attributes: {
        paymentSummary: {
          total,
          subtotal,
          payToday,
          payLater,
          vatLabel,
          gstLabel,
          dutyLabel,
          siteCredit,
          salesLabel,
          estimatedVat,
          estimatedGst,
          rewardsAmount,
          estimatedDuty,
          estimatedSalesTax,
          estimatedShipping,
          promotionDiscount,
          isShippingPromotionApplied
        }
      }
    }
  } = useCart();
  const { canProceedToCheckout, availableNowItems, preorderItems } = useCartItems();

  const isMobile = useBreakpoint({ lt: 'sm' }).matches;
  const hideCheckoutButtons = isMobile;
  const showApplePayButton =
    availableNowItems.length > 0 && preorderItems.length <= 0 && Number(payToday.price) > 0;

  const orderTotal =
    Number(total.price) - (Number(siteCredit.price) || 0) - (Number(rewardsAmount.price) || 0);

  useUsDomesticShippingFeeOptimization();
  useInternationalDomesticShippingFeeOptimization();

  return (
    <Stack space={4} className="ShoppingBagPaymentSummary">
      <Text treatment="h4" family="serif">
        Payment Summary
      </Text>
      <Divider />
      <Stack space={3}>
        <PaymentSummaryRow
          label="Order Subtotal"
          value={formatPrice(subtotal.currency, subtotal.price)}
        />
        {estimatedDuty && (
          <PaymentSummaryTaxRow
            label={dutyLabel}
            value={formatPrice(estimatedDuty.currency, estimatedDuty.price)}
          />
        )}
        {estimatedVat && (
          <PaymentSummaryTaxRow
            label={vatLabel}
            value={formatPrice(estimatedVat.currency, estimatedVat.price)}
          />
        )}
        {estimatedGst && (
          <PaymentSummaryTaxRow
            label={gstLabel}
            value={formatPrice(estimatedGst.currency, estimatedGst.price)}
          />
        )}

        {estimatedSalesTax && (
          <PaymentSummaryTaxRow
            label={salesLabel}
            value={formatPrice(estimatedSalesTax.currency, estimatedSalesTax.price)}
          />
        )}

        <PaymentSummaryRow
          label="Est. Shipping"
          tooltip="We estimate shipping fees using your location. You will be able to change your shipping method before completing your order."
          value={
            isShippingPromotionApplied
              ? 'Free'
              : formatPrice(estimatedShipping.currency, estimatedShipping.price)
          }
        />

        <RewardSummary theme="simple" format="negative" />

        <GiftCode theme="simple" promotionDiscount={promotionDiscount} />

        {Number(siteCredit.price) > 0 && (
          <PaymentSummaryRow
            label="Site Credit"
            value={`-${formatPrice(siteCredit.currency, siteCredit.price)}`}
          />
        )}
      </Stack>
      <div className="ShoppingBagPaymentSummary__total">
        <PaymentSummaryRow label="Order Total" value={formatPrice(total.currency, orderTotal)} />
      </div>
      <Stack space={3}>
        <Stack space={1} className="ShoppingBagPaymentSummary__pay-today">
          <div className="ShoppingBagPaymentSummary__pay-today-title" data-testid="Pay Today">
            <PaymentSummaryRow
              label="Est. Pay Today"
              value={formatPrice(payToday.currency, payToday.price)}
            />
          </div>
          {Number(payLater.price) === 0 && (
            <div className="ShoppingBagPaymentSummary__affirm-promo">
              <AffirmPromoMessage pageType="cart" price={Number(payToday.price)} />
            </div>
          )}
        </Stack>
        {Number(payLater.price) > 0 && (
          <PaymentSummaryRow
            label="Est. Remaining Balance"
            tooltip="Preorder items are made especially for you and require a 50% deposit at the time of purchase. The remaining balance and sales tax/duties will be charged upon shipping."
            tooltipAnchor="bottomCenter"
            value={formatPrice(payLater.currency, payLater.price)}
          />
        )}
      </Stack>
      {!hideCheckoutButtons && (
        <>
          <Button
            to={hrefFor.CheckoutPage()}
            disabled={canProceedToCheckout}
            data-testid="shoppingBagCheckoutButton"
          >
            Checkout
          </Button>
          <div className="ShoppingBagPaymentSummary__divider">
            <Text className="ShoppingBagPaymentSummary__divider-text">OR</Text>
          </div>

          <Text>
            Skip the forms and use your saved information. By proceeding to check out via any
            payment method you agree to our{' '}
            <ControlLink to={hrefFor.TermsAndConditionsPage()} underlined target="_blank">
              Terms & Conditions
            </ControlLink>{' '}
            and{' '}
            <ControlLink to={hrefFor.PrivacyPolicyPage()} underlined target="_blank">
              Privacy Policy
            </ControlLink>
            .
          </Text>
          <div className="ShoppingBagPaymentSummary__payment-methods">
            <CheckoutPayPalButton />
            {showApplePayButton && <CheckoutApplePayButton />}
            <CheckoutAmazonPayButton />
          </div>
        </>
      )}
    </Stack>
  );
};
